import type { AuthProvider } from '@communities/community/domain';
import { useAuthProviders } from '@communities/community/hooks';
import { AuthenticationProviderDialog, AuthenticationProvidersMoreInfoDialog } from '@communities/content-management/components';
import { DataList, type TableColumn } from '@core/components';
import { SectionHeader } from '@core/components';
import { Text, Badge, IconButton, Info16, IconButtonSize, IconSize } from '@soteria/design-system-ve';
import { useState, useCallback } from 'react';

export function AuthenticationProvidersSection() {
  const { providers, loading, errors } = useAuthProviders();
  const [selectedProvider, setSelectedProvider] = useState<AuthProvider | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);

  const handleOpenDialog = useCallback((provider: AuthProvider) => {
    setSelectedProvider(provider);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedProvider(null);
  }, []);

  const columns: TableColumn<AuthProvider>[] = [
    {
      key: 'name',
      header: 'Provider Name',
      render: provider => <Text>{provider.name}</Text>,
      width: '30%',
    },
    {
      key: 'clientId',
      header: 'Client ID',
      render: provider => <Text>{provider.clientId}</Text>,
      width: '25%',
    },
    {
      key: 'status',
      header: 'Status',
      render: provider => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Badge text={provider.providerStatus} />
          {provider.isDefaultProvider && <Badge text="Default" />}
        </div>
      ),
      width: '35%',
    },
    {
      key: 'created',
      header: 'Created',
      render: provider => <Text>{new Date(provider.createdAt).toLocaleDateString()}</Text>,
      width: '10%',
    },
  ];

  return (
    <>
      <SectionHeader
        description="Configured authentication providers for this community. These providers handle user authentication and authorization."
        endComponent={
          <IconButton
            aria-label="Learn more about authentication providers"
            buttonSize={IconButtonSize.Medium}
            icon={Info16}
            iconSize={IconSize.XSmall}
            onClick={() => setIsMoreInfoDialogOpen(true)}
          />
        }
        title={`Authentication Providers (${providers.length})`}
      />

      {/* Authentication Providers List*/}
      <DataList columns={columns} data={providers} dataTypeName="authentication provider" error={errors} isLoading={loading} onView={handleOpenDialog} />

      {/* Create/Edit Authentication Provider Dialog */}
      <AuthenticationProviderDialog isOpen={isDialogOpen} provider={selectedProvider} onClose={handleCloseDialog} />

      {/* More Info Dialog */}
      <AuthenticationProvidersMoreInfoDialog isOpen={isMoreInfoDialogOpen} onClose={() => setIsMoreInfoDialogOpen(false)} />
    </>
  );
}
